/* ISQM registers (deficiencies, remediations, complaints, inspections) with their dialogs — moved out of
   pages/ISQM.jsx in split step 5b (28-Oct-2026), unchanged. */
import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import { useEffect, useState } from "react";
import DateInput from "@/components/DateInput";
import { Link } from "react-router-dom";
import api, { toErrMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  AlertTriangle, Info,
  Trash2, Pencil,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { askConfirm } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)


// ======================================================================
// Registers section — CRUD for 4 entity-backed register tables
// (deficiencies / remediations / complaints / inspections)
// ======================================================================
function RegistersSection() {
  const { t } = useI18n();
  const [tab, setTab] = useState("deficiencies");
  return (
    <Tabs value={tab} onValueChange={setTab}>
      <TabsList>
        <TabsTrigger value="deficiencies" data-testid="reg-tab-def">{t("isqmPage.regTabDef")}</TabsTrigger>
        <TabsTrigger value="remediations" data-testid="reg-tab-rem">{t("isqmPage.regTabRem")}</TabsTrigger>
        <TabsTrigger value="complaints" data-testid="reg-tab-comp">{t("isqmPage.regTabComp")}</TabsTrigger>
        <TabsTrigger value="inspections" data-testid="reg-tab-insp">{t("isqmPage.regTabInsp")}</TabsTrigger>
      </TabsList>
      <TabsContent value="deficiencies"><DeficiencyRegister /></TabsContent>
      <TabsContent value="remediations"><RemediationRegister /></TabsContent>
      <TabsContent value="complaints"><ComplaintRegister /></TabsContent>
      <TabsContent value="inspections"><InspectionRegister /></TabsContent>
    </Tabs>
  );
}


const STATUS_TONE = {
  open: "bg-red-100 text-red-800",
  pending_effectiveness_review: "bg-amber-100 text-amber-800",
  remediated: "bg-emerald-100 text-emerald-800",
  planned: "bg-slate-100 text-slate-800",
  in_progress: "bg-blue-100 text-blue-800",
  completed: "bg-emerald-100 text-emerald-800",
  under_investigation: "bg-amber-100 text-amber-800",
  resolved: "bg-emerald-100 text-emerald-800",
};


function DeficiencyRegister() {
  const colW = useResizableColumns({ storageKey: "isqm-deficiency-register", defaults: [240, 110, 160, 110, 100], mins: [130, 72, 100, 72, 60] });   // Step 11d · add-on
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [impact, setImpact] = useState(null);   // {row, refs}
  const load = () => api.get("/isqm/registers/deficiencies")
    .then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);
  const del = async (row) => {
    if (!await askConfirm(t("isqmPage.defConfirmDelete").replace("{desc}", row.description.slice(0, 50)))) return;
    try { await api.delete(`/isqm/registers/deficiencies/${row.id}`); toast.success(t("isqmPage.toastDeleted")); load(); }
    catch (e) {
      if (e?.response?.status === 409 && e.response.data?.detail?.remediations !== undefined) {
        setImpact({ row, refs: e.response.data.detail });
      } else {
        toast.error(toErrMsg(e, t("isqmPage.toastDeleteFailed")));
      }
    }
  };
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">{t("isqmPage.defTitle")}</CardTitle>
        <Button size="sm" onClick={() => setDlg({ mode: "create", row: { source: "other", status: "open" } })}
                data-testid="def-add-btn">{t("isqmPage.defAdd")}</Button>
      </CardHeader>
      <CardContent className="p-0">
        <ResizableTable cols={colW} wrap className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground">
            <tr>
              <ResizableTh index={0} className="px-3 py-2 text-start">{t("isqmPage.defColDesc")}</ResizableTh>
              <ResizableTh index={1} className="px-3 py-2 text-start">{t("isqmPage.defColSource")}</ResizableTh>
              <ResizableTh index={2} className="px-3 py-2 text-start">{t("isqmPage.defColSeverity")}</ResizableTh>
              <ResizableTh index={3} className="px-3 py-2 text-start">{t("isqmPage.defColStatus")}</ResizableTh>
              <ResizableTh index={4} className="w-24"></ResizableTh>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">{t("isqmPage.defEmpty")}</td></tr>
            )}
            {items.map((r) => (
              <tr key={r.id} className="border-b" data-testid={`def-row-${r.id}`}>
                <td className="px-3 py-2 max-w-[400px]">
                  <div className="font-medium">{r.description}</div>
                  {r.root_cause && <div className="text-[11px] text-muted-foreground">{t("isqmPage.defRootCausePrefix")} {r.root_cause}</div>}
                </td>
                <td className="px-3 py-2 text-xs">{r.source}</td>
                <td className="px-3 py-2 text-xs">{r.severity || "—"}</td>
                <td className="px-3 py-2">
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_TONE[r.status] || ""}`}>
                    {t(`isqmPage.statuses.${r.status}`) || r.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-end">
                  <button onClick={() => setDlg({ mode: "edit", row: r })}
                          className="rounded p-1 hover:bg-slate-100" title={t("isqmPage.actEdit")}
                          data-testid={`def-edit-${r.id}`}>
                    <Pencil className="h-3.5 w-3.5 text-slate-500" />
                  </button>
                  <button onClick={() => del(r)} className="ms-1 rounded p-1 hover:bg-red-50" title={t("isqmPage.actDelete")}
                          data-testid={`def-del-${r.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </ResizableTable>
      </CardContent>
      {dlg && <DeficiencyDialog dlg={dlg} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); load(); }} />}
      {impact && <DeleteImpactDialog impact={impact} onClose={() => setImpact(null)} />}
    </Card>
  );
}


function DeleteImpactDialog({ impact, onClose }) {
  const { t } = useI18n();
  const { row, refs } = impact;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="def-impact-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" /> {t("isqmPage.impactCannotDelete")}
          </DialogTitle>
          <DialogDescription>
            {t("isqmPage.impactRefIntroPre").replace("{desc}", row.description.slice(0, 100))}
            {" "}<b>{refs.total}</b> {t("isqmPage.impactRefIntroPost")}
          </DialogDescription>
        </DialogHeader>
        {refs.remediations?.length > 0 && (
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t("isqmPage.impactRemsLabel").replace("{n}", refs.remediations.length)}
            </div>
            <div className="mt-1 max-h-40 overflow-y-auto rounded border">
              {refs.remediations.map((r) => (
                <div key={r.id} className="border-b px-3 py-1.5 text-xs" data-testid={`impact-rem-${r.id}`}>
                  <span className="font-medium">{r.action}</span>
                  <span className="ms-2 text-muted-foreground">{t("isqmPage.impactOwnerPrefix")} {r.owner || "—"} · {r.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {refs.complaints?.length > 0 && (
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t("isqmPage.impactCompsLabel").replace("{n}", refs.complaints.length)}
            </div>
            <div className="mt-1 max-h-40 overflow-y-auto rounded border">
              {refs.complaints.map((c) => (
                <div key={c.id} className="border-b px-3 py-1.5 text-xs" data-testid={`impact-comp-${c.id}`}>
                  <span className="font-medium">{c.nature}</span>
                  <span className="ms-2 text-muted-foreground">· {c.date_received} · {c.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {refs.inspections?.length > 0 && (
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t("isqmPage.impactInspsLabel").replace("{n}", refs.inspections.length)}
            </div>
            <div className="mt-1 max-h-40 overflow-y-auto rounded border">
              {refs.inspections.map((i) => (
                <div key={i.id} className="border-b px-3 py-1.5 text-xs" data-testid={`impact-insp-${i.id}`}>
                  <span className="font-medium">{i.engagement_inspected}</span>
                  <span className="ms-2 text-muted-foreground">· {i.date || t("isqmPage.impactNoDate")} {t("isqmPage.impactInspectorPrefix")} {i.inspector || "—"}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button onClick={onClose} data-testid="impact-close">{t("isqmPage.gotIt")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function DeficiencyDialog({ dlg, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState(dlg.row);
  const isEdit = dlg.mode === "edit";
  const submit = async () => {
    if (!form.description?.trim()) { toast.error(t("isqmPage.toastDescRequired")); return; }
    try {
      if (isEdit) await api.patch(`/isqm/registers/deficiencies/${form.id}`, form);
      else await api.post("/isqm/registers/deficiencies", form);
      toast.success(t("isqmPage.toastSaved"));
      onSaved();
    } catch (e) { toast.error(toErrMsg(e, t("isqmPage.toastSaveFailed"))); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="def-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? t("isqmPage.defDialogTitleEdit") : t("isqmPage.defDialogTitleAdd")}</DialogTitle>
          <DialogDescription>{t("isqmPage.defDialogDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t("isqmPage.defLabelDescription")}</Label>
            <textarea rows={3} className="w-full rounded-md border p-2 text-sm dark:bg-slate-900"
                      value={form.description || ""}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      data-testid="def-desc" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>{t("isqmPage.defLabelSource")}</Label>
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger data-testid="def-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inspection">{t("isqmPage.defSourceInsp")}</SelectItem>
                  <SelectItem value="complaint">{t("isqmPage.defSourceComp")}</SelectItem>
                  <SelectItem value="other">{t("isqmPage.defSourceOther")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("isqmPage.defLabelSeverity")}</Label>
              <Input value={form.severity || ""} onChange={(e) => setForm({ ...form, severity: e.target.value })}
                     data-testid="def-severity" placeholder={t("isqmPage.defSeverityPlaceholder")} />
            </div>
          </div>
          <div>
            <Label>{t("isqmPage.defLabelRootCause")}</Label>
            <Input value={form.root_cause || ""} onChange={(e) => setForm({ ...form, root_cause: e.target.value })}
                   data-testid="def-root" />
          </div>
          {isEdit && (
            <div>
              <Label>{t("isqmPage.defLabelStatus")}</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="def-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">{t("isqmPage.defStatusOpen")}</SelectItem>
                  <SelectItem value="pending_effectiveness_review">{t("isqmPage.defStatusPendEff")}</SelectItem>
                  <SelectItem value="remediated">{t("isqmPage.defStatusRem")}</SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-1 text-[10px] text-muted-foreground">
                {t("isqmPage.defRemHint")}
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("isqmPage.cancel")}</Button>
          <Button onClick={submit} className="bg-slate-900 hover:bg-slate-800" data-testid="def-save">
            {isEdit ? t("isqmPage.save") : t("isqmPage.add")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function RemediationRegister() {
  const colW2 = useResizableColumns({ storageKey: "isqm-remediation-register", defaults: [160, 130, 160, 130, 110, 160, 100], mins: [100, 90, 100, 96, 72, 100, 60] });   // Step 11d · add-on
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [defs, setDefs] = useState([]);
  const [dlg, setDlg] = useState(null);
  const load = () => Promise.all([
    api.get("/isqm/registers/remediations"),
    api.get("/isqm/registers/deficiencies"),
  ]).then(([r, d]) => { setItems(r.data.items || []); setDefs(d.data.items || []); });
  useEffect(() => { load(); }, []);
  const del = async (row) => {
    if (!await askConfirm(t("isqmPage.remConfirmDelete"))) return;
    try { await api.delete(`/isqm/registers/remediations/${row.id}`); load(); toast.success(t("isqmPage.toastDeleted")); }
    catch (e) { toast.error(toErrMsg(e, t("isqmPage.toastDeleteFailed"))); }
  };
  const defById = Object.fromEntries(defs.map((d) => [d.id, d]));
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">{t("isqmPage.remTitle")}</CardTitle>
        <Button size="sm" onClick={() => setDlg({ mode: "create", row: { status: "planned" } })}
                data-testid="rem-add-btn" disabled={defs.length === 0}>{t("isqmPage.remAdd")}</Button>
      </CardHeader>
      <CardContent className="p-0">
        {defs.length === 0 && (
          <div className="px-4 py-3 text-[12px] text-amber-700">
            <Info className="me-1 inline h-3 w-3" /> {t("isqmPage.remAddDefFirst")}
          </div>
        )}
        <ResizableTable cols={colW2} wrap className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground">
            <tr>
              <ResizableTh index={0} className="px-3 py-2 text-start">{t("isqmPage.remColDef")}</ResizableTh>
              <ResizableTh index={1} className="px-3 py-2 text-start">{t("isqmPage.remColAction")}</ResizableTh>
              <ResizableTh index={2} className="px-3 py-2 text-start">{t("isqmPage.remColOwner")}</ResizableTh>
              <ResizableTh index={3} className="px-3 py-2 text-start">{t("isqmPage.remColDate")}</ResizableTh>
              <ResizableTh index={4} className="px-3 py-2 text-start">{t("isqmPage.remColStatus")}</ResizableTh>
              <ResizableTh index={5} className="px-3 py-2 text-start">{t("isqmPage.remColEff")}</ResizableTh>
              <ResizableTh index={6} className="w-24"></ResizableTh>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">{t("isqmPage.remEmpty")}</td></tr>
            )}
            {items.map((r) => (
              <tr key={r.id} className="border-b" data-testid={`rem-row-${r.id}`}>
                <td className="px-3 py-2 max-w-[280px] text-xs">
                  {defById[r.deficiency_id]?.description || <em className="text-red-500">{t("isqmPage.remDeleted")}</em>}
                </td>
                <td className="px-3 py-2">{r.action}</td>
                <td className="px-3 py-2 text-xs">{r.owner || "—"}</td>
                <td className="px-3 py-2 text-xs tabular-nums">{r.target_date || "—"}</td>
                <td className="px-3 py-2">
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_TONE[r.status] || ""}`}>
                    {t(`isqmPage.statuses.${r.status}`) || r.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs">
                  {r.effectiveness_confirmed
                    ? <span className="text-emerald-700">{t("isqmPage.remConfirmed")}</span>
                    : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2 text-end">
                  <button onClick={() => setDlg({ mode: "edit", row: r })} className="rounded p-1 hover:bg-slate-100"
                          data-testid={`rem-edit-${r.id}`}><Pencil className="h-3.5 w-3.5 text-slate-500" /></button>
                  <button onClick={() => del(r)} className="ms-1 rounded p-1 hover:bg-red-50"
                          data-testid={`rem-del-${r.id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </ResizableTable>
      </CardContent>
      {dlg && <RemediationDialog dlg={dlg} defs={defs} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); load(); }} />}
    </Card>
  );
}


function RemediationDialog({ dlg, defs, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState(dlg.row);
  const isEdit = dlg.mode === "edit";
  const submit = async () => {
    if (!form.deficiency_id) { toast.error(t("isqmPage.toastPickDefLink")); return; }
    if (!form.action?.trim()) { toast.error(t("isqmPage.toastActionRequired")); return; }
    try {
      if (isEdit) await api.patch(`/isqm/registers/remediations/${form.id}`, form);
      else await api.post("/isqm/registers/remediations", form);
      toast.success(t("isqmPage.toastSaved"));
      onSaved();
    } catch (e) { toast.error(toErrMsg(e, t("isqmPage.toastSaveFailed"))); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="rem-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? t("isqmPage.remDialogTitleEdit") : t("isqmPage.remDialogTitleAdd")}</DialogTitle>
          <DialogDescription>{t("isqmPage.remDialogDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t("isqmPage.remLabelDef")}</Label>
            <Select value={form.deficiency_id || ""}
                     onValueChange={(v) => setForm({ ...form, deficiency_id: v })}>
              <SelectTrigger data-testid="rem-def-picker"><SelectValue placeholder={t("isqmPage.remDefPlaceholder")} /></SelectTrigger>
              <SelectContent>
                {defs.filter((d) => d.status !== "remediated").map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    [{d.status}] {d.description.slice(0, 80)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("isqmPage.remLabelAction")}</Label>
            <textarea rows={2} className="w-full rounded-md border p-2 text-sm dark:bg-slate-900"
                      value={form.action || ""} onChange={(e) => setForm({ ...form, action: e.target.value })}
                      data-testid="rem-action" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-1">
              <Label>{t("isqmPage.remLabelOwner")}</Label>
              <Input value={form.owner || ""} onChange={(e) => setForm({ ...form, owner: e.target.value })}
                     data-testid="rem-owner" />
            </div>
            <div className="col-span-1">
              <Label>{t("isqmPage.remLabelDate")}</Label>
              <DateInput value={form.target_date || ""}
                     onChange={(v) => setForm({ ...form, target_date: v })}
                     data-testid="rem-date" />
            </div>
            <div className="col-span-1">
              <Label>{t("isqmPage.remLabelStatus")}</Label>
              <Select value={form.status || "planned"} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="rem-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">{t("isqmPage.remStatusPlanned")}</SelectItem>
                  <SelectItem value="in_progress">{t("isqmPage.remStatusInProgress")}</SelectItem>
                  <SelectItem value="completed">{t("isqmPage.remStatusCompleted")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {isEdit && (
            <div className="rounded border bg-slate-50 p-2 dark:bg-slate-900/40">
              <label className="inline-flex items-center gap-2 text-xs">
                <input type="checkbox" checked={!!form.effectiveness_confirmed}
                       onChange={(e) => setForm({ ...form, effectiveness_confirmed: e.target.checked })}
                       data-testid="rem-eff-cbx" />
                {t("isqmPage.remEffConfirmed")}
              </label>
              {form.effectiveness_confirmed && (
                <Input className="mt-2" placeholder={t("isqmPage.remEffEvidence")}
                       value={form.effectiveness_evidence_ref || ""}
                       onChange={(e) => setForm({ ...form, effectiveness_evidence_ref: e.target.value })}
                       data-testid="rem-eff-ref" />
              )}
            </div>
          )}
          <p className="text-[10px] text-muted-foreground">
            {t("isqmPage.remCompletedNote")}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("isqmPage.cancel")}</Button>
          <Button onClick={submit} className="bg-slate-900 hover:bg-slate-800" data-testid="rem-save">
            {isEdit ? t("isqmPage.save") : t("isqmPage.add")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ComplaintRegister() {
  const colW3 = useResizableColumns({ storageKey: "isqm-complaint-register", defaults: [130, 160, 110, 160, 110, 100], mins: [96, 100, 72, 100, 72, 60] });   // Step 11d · add-on
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [defs, setDefs] = useState([]);
  const [dlg, setDlg] = useState(null);
  const load = () => Promise.all([
    api.get("/isqm/registers/complaints"),
    api.get("/isqm/registers/deficiencies"),
  ]).then(([c, d]) => { setItems(c.data.items || []); setDefs(d.data.items || []); });
  useEffect(() => { load(); }, []);
  const del = async (row) => {
    if (!await askConfirm(t("isqmPage.compConfirmDelete"))) return;
    try { await api.delete(`/isqm/registers/complaints/${row.id}`); load(); toast.success(t("isqmPage.toastDeleted")); }
    catch (e) { toast.error(toErrMsg(e, t("isqmPage.toastDeleteFailed"))); }
  };
  const defById = Object.fromEntries(defs.map((d) => [d.id, d]));
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">{t("isqmPage.compTitle")}</CardTitle>
        <Button size="sm" onClick={() => setDlg({ mode: "create", row: { status: "open", date_received: new Date().toISOString().slice(0,10) } })}
                data-testid="comp-add-btn">{t("isqmPage.compAdd")}</Button>
      </CardHeader>
      <CardContent className="p-0">
        <ResizableTable cols={colW3} wrap className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground">
            <tr>
              <ResizableTh index={0} className="px-3 py-2 text-start">{t("isqmPage.compColDate")}</ResizableTh>
              <ResizableTh index={1} className="px-3 py-2 text-start">{t("isqmPage.compColNature")}</ResizableTh>
              <ResizableTh index={2} className="px-3 py-2 text-start">{t("isqmPage.compColSource")}</ResizableTh>
              <ResizableTh index={3} className="px-3 py-2 text-start">{t("isqmPage.compColLinked")}</ResizableTh>
              <ResizableTh index={4} className="px-3 py-2 text-start">{t("isqmPage.compColStatus")}</ResizableTh>
              <ResizableTh index={5} className="w-24"></ResizableTh>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">{t("isqmPage.compEmpty")}</td></tr>
            )}
            {items.map((r) => (
              <tr key={r.id} className="border-b" data-testid={`comp-row-${r.id}`}>
                <td className="px-3 py-2 text-xs tabular-nums">{r.date_received}</td>
                <td className="px-3 py-2 max-w-[300px]">{r.nature}</td>
                <td className="px-3 py-2 text-xs">{r.source || "—"}</td>
                <td className="px-3 py-2 text-xs max-w-[220px]">
                  {r.linked_deficiency_id
                    ? (defById[r.linked_deficiency_id]?.description?.slice(0, 60) || <em>{t("isqmPage.compDeleted")}</em>)
                    : "—"}
                </td>
                <td className="px-3 py-2">
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_TONE[r.status] || ""}`}>
                    {t(`isqmPage.statuses.${r.status}`) || r.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-end">
                  <button onClick={() => setDlg({ mode: "edit", row: r })} className="rounded p-1 hover:bg-slate-100"
                          data-testid={`comp-edit-${r.id}`}><Pencil className="h-3.5 w-3.5 text-slate-500" /></button>
                  <button onClick={() => del(r)} className="ms-1 rounded p-1 hover:bg-red-50"
                          data-testid={`comp-del-${r.id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </ResizableTable>
      </CardContent>
      {dlg && <ComplaintDialog dlg={dlg} defs={defs} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); load(); }} />}
    </Card>
  );
}


function ComplaintDialog({ dlg, defs, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState(dlg.row);
  const isEdit = dlg.mode === "edit";
  const submit = async () => {
    if (!form.date_received) { toast.error(t("isqmPage.toastDateRequired")); return; }
    if (!form.nature?.trim()) { toast.error(t("isqmPage.toastNatureRequired")); return; }
    try {
      if (isEdit) await api.patch(`/isqm/registers/complaints/${form.id}`, form);
      else await api.post("/isqm/registers/complaints", form);
      toast.success(t("isqmPage.toastSaved"));
      onSaved();
    } catch (e) { toast.error(toErrMsg(e, t("isqmPage.toastSaveFailed"))); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="comp-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? t("isqmPage.compDialogTitleEdit") : t("isqmPage.compDialogTitleAdd")}</DialogTitle>
          <DialogDescription>{t("isqmPage.compDialogDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>{t("isqmPage.compLabelDate")}</Label>
              <DateInput value={form.date_received || ""}
                     onChange={(v) => setForm({ ...form, date_received: v })}
                     data-testid="comp-date" />
            </div>
            <div>
              <Label>{t("isqmPage.compLabelSource")}</Label>
              <Input value={form.source || ""} onChange={(e) => setForm({ ...form, source: e.target.value })}
                     data-testid="comp-source" placeholder={t("isqmPage.compSourcePlaceholder")} />
            </div>
          </div>
          <div>
            <Label>{t("isqmPage.compLabelNature")}</Label>
            <textarea rows={2} className="w-full rounded-md border p-2 text-sm dark:bg-slate-900"
                      value={form.nature || ""} onChange={(e) => setForm({ ...form, nature: e.target.value })}
                      data-testid="comp-nature" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>{t("isqmPage.compLabelInvBy")}</Label>
              <Input value={form.investigated_by || ""}
                     onChange={(e) => setForm({ ...form, investigated_by: e.target.value })}
                     data-testid="comp-invby" />
            </div>
            <div>
              <Label>{t("isqmPage.compLabelStatus")}</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="comp-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">{t("isqmPage.compStatusOpen")}</SelectItem>
                  <SelectItem value="under_investigation">{t("isqmPage.compStatusUnder")}</SelectItem>
                  <SelectItem value="resolved">{t("isqmPage.compStatusResolved")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>{t("isqmPage.compLabelOutcome")}</Label>
            <Input value={form.outcome || ""} onChange={(e) => setForm({ ...form, outcome: e.target.value })}
                   data-testid="comp-outcome" />
          </div>
          <div>
            <Label>{t("isqmPage.compLabelLinked")}</Label>
            <Select value={form.linked_deficiency_id || "__none__"}
                     onValueChange={(v) => setForm({ ...form, linked_deficiency_id: v === "__none__" ? null : v })}>
              <SelectTrigger data-testid="comp-def-picker"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{t("isqmPage.compNoLink")}</SelectItem>
                {defs.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    [{d.status}] {d.description.slice(0, 80)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-[10px] text-muted-foreground">
              {t("isqmPage.compLinkedHint")}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("isqmPage.cancel")}</Button>
          <Button onClick={submit} className="bg-slate-900 hover:bg-slate-800" data-testid="comp-save">
            {isEdit ? t("isqmPage.save") : t("isqmPage.add")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function InspectionRegister() {
  const colW4 = useResizableColumns({ storageKey: "isqm-inspection-register", defaults: [160, 160, 130, 160, 160, 100], mins: [100, 100, 96, 100, 100, 60] });   // Step 11d · add-on
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [defs, setDefs] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [esc, setEsc] = useState(null);
  const load = () => Promise.all([
    api.get("/isqm/registers/inspections"),
    api.get("/isqm/registers/deficiencies"),
  ]).then(([i, d]) => { setItems(i.data.items || []); setDefs(d.data.items || []); });
  useEffect(() => { load(); }, []);
  const del = async (row) => {
    if (!await askConfirm(t("isqmPage.inspConfirmDelete"))) return;
    try { await api.delete(`/isqm/registers/inspections/${row.id}`); load(); toast.success(t("isqmPage.toastDeleted")); }
    catch (e) { toast.error(toErrMsg(e, t("isqmPage.toastDeleteFailed"))); }
  };
  const unlink = async (row) => {
    if (!await askConfirm(t("isqmPage.inspConfirmUnlink"))) return;
    try {
      await api.patch(`/isqm/registers/inspections/${row.id}`, { created_deficiency_id: "" });
      toast.success(t("isqmPage.toastUnlinked")); load();
    } catch (e) { toast.error(toErrMsg(e, t("isqmPage.toastUnlinkFailed"))); }
  };
  const defById = Object.fromEntries(defs.map((d) => [d.id, d]));
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">{t("isqmPage.inspTitle")}</CardTitle>
        <Button size="sm" onClick={() => setDlg({ mode: "create", row: {} })}
                data-testid="insp-add-btn">{t("isqmPage.inspAdd")}</Button>
      </CardHeader>
      <CardContent className="p-0">
        <ResizableTable cols={colW4} wrap className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground">
            <tr>
              <ResizableTh index={0} className="px-3 py-2 text-start">{t("isqmPage.inspColEng")}</ResizableTh>
              <ResizableTh index={1} className="px-3 py-2 text-start">{t("isqmPage.inspColPartner")}</ResizableTh>
              <ResizableTh index={2} className="px-3 py-2 text-start">{t("isqmPage.inspColDate")}</ResizableTh>
              <ResizableTh index={3} className="px-3 py-2 text-start">{t("isqmPage.inspColFindings")}</ResizableTh>
              <ResizableTh index={4} className="px-3 py-2 text-start">{t("isqmPage.inspColEsc")}</ResizableTh>
              <ResizableTh index={5} className="w-40"></ResizableTh>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">{t("isqmPage.inspEmpty")}</td></tr>
            )}
            {items.map((r) => {
              const d = r.created_deficiency_id ? defById[r.created_deficiency_id] : null;
              return (
                <tr key={r.id} className="border-b" data-testid={`insp-row-${r.id}`}>
                  <td className="px-3 py-2">{r.engagement_inspected}</td>
                  <td className="px-3 py-2 text-xs">{r.partner || "—"}</td>
                  <td className="px-3 py-2 text-xs tabular-nums">{r.date || "—"}</td>
                  <td className="px-3 py-2 max-w-[280px] text-xs">{r.findings_summary || "—"}</td>
                  <td className="px-3 py-2">
                    {d ? (
                      <Link to="/isqm" className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-100">
                        {t("isqmPage.inspLinkDef")}
                        <span className={`rounded px-1 py-0.5 text-[9px] font-semibold ${STATUS_TONE[d.status] || ""}`}>
                          {t(`isqmPage.statuses.${d.status}`) || d.status}
                        </span>
                      </Link>
                    ) : (
                      <span className="text-[11px] text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-end">
                    {!d ? (
                      <Button size="sm" variant="outline" onClick={() => setEsc(r)}
                              data-testid={`insp-escalate-${r.id}`}>
                        {t("isqmPage.inspEscalate")}
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => unlink(r)} className="text-amber-700"
                              data-testid={`insp-unlink-${r.id}`}>
                        {t("isqmPage.inspUnlink")}
                      </Button>
                    )}
                    <button onClick={() => del(r)} className="ms-1 rounded p-1 hover:bg-red-50"
                            data-testid={`insp-del-${r.id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </ResizableTable>
      </CardContent>
      {dlg && <InspectionDialog dlg={dlg} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); load(); }} />}
      {esc && <EscalateDialog inspection={esc} defs={defs} onClose={() => setEsc(null)} onDone={() => { setEsc(null); load(); }} />}
    </Card>
  );
}


function InspectionDialog({ dlg, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState(dlg.row);
  const isEdit = dlg.mode === "edit";
  const submit = async () => {
    if (!form.engagement_inspected?.trim()) { toast.error(t("isqmPage.toastEngInspRequired")); return; }
    try {
      if (isEdit) await api.patch(`/isqm/registers/inspections/${form.id}`, form);
      else await api.post("/isqm/registers/inspections", form);
      toast.success(t("isqmPage.toastSaved"));
      onSaved();
    } catch (e) { toast.error(toErrMsg(e, t("isqmPage.toastSaveFailed"))); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="insp-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? t("isqmPage.inspDialogTitleEdit") : t("isqmPage.inspDialogTitleAdd")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t("isqmPage.inspLabelEng")}</Label>
            <Input value={form.engagement_inspected || ""}
                   onChange={(e) => setForm({ ...form, engagement_inspected: e.target.value })}
                   data-testid="insp-eng" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>{t("isqmPage.inspLabelPartner")}</Label>
              <Input value={form.partner || ""} onChange={(e) => setForm({ ...form, partner: e.target.value })}
                     data-testid="insp-partner" />
            </div>
            <div>
              <Label>{t("isqmPage.inspLabelInspector")}</Label>
              <Input value={form.inspector || ""} onChange={(e) => setForm({ ...form, inspector: e.target.value })}
                     data-testid="insp-inspector" />
            </div>
            <div>
              <Label>{t("isqmPage.inspLabelDate")}</Label>
              <DateInput value={form.date || ""} onChange={(v) => setForm({ ...form, date: v })}
                     data-testid="insp-date" />
            </div>
          </div>
          <div>
            <Label>{t("isqmPage.inspLabelFindings")}</Label>
            <textarea rows={3} className="w-full rounded-md border p-2 text-sm dark:bg-slate-900"
                      value={form.findings_summary || ""}
                      onChange={(e) => setForm({ ...form, findings_summary: e.target.value })}
                      data-testid="insp-findings" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("isqmPage.cancel")}</Button>
          <Button onClick={submit} className="bg-slate-900 hover:bg-slate-800" data-testid="insp-save">
            {isEdit ? t("isqmPage.save") : t("isqmPage.add")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EscalateDialog({ inspection, defs, onClose, onDone }) {
  const { t } = useI18n();
  const [mode, setMode] = useState("create");
  const [defId, setDefId] = useState("");
  const [desc, setDesc] = useState(inspection.findings_summary || "");
  const submit = async () => {
    try {
      const body = mode === "link" ? { mode: "link", deficiency_id: defId }
                                     : { mode: "create", description: desc };
      if (mode === "link" && !defId) { toast.error(t("isqmPage.toastPickDef")); return; }
      if (mode === "create" && !desc?.trim()) { toast.error(t("isqmPage.toastDescRequired")); return; }
      await api.post(`/isqm/registers/inspections/${inspection.id}/escalate`, body);
      toast.success(t("isqmPage.toastEscLogged"));
      onDone();
    } catch (e) { toast.error(toErrMsg(e, t("isqmPage.toastEscFailed"))); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="insp-esc-dialog">
        <DialogHeader>
          <DialogTitle>{t("isqmPage.escTitle")}</DialogTitle>
          <DialogDescription>
            {t("isqmPage.escDesc")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="inline-flex rounded-md border bg-slate-50 p-0.5 text-xs dark:bg-slate-900">
            <button onClick={() => setMode("create")}
                    className={`rounded px-3 py-1 font-medium ${mode === "create" ? "bg-slate-900 text-white" : ""}`}
                    data-testid="esc-mode-create">{t("isqmPage.escModeCreate")}</button>
            <button onClick={() => setMode("link")}
                    className={`rounded px-3 py-1 font-medium ${mode === "link" ? "bg-slate-900 text-white" : ""}`}
                    data-testid="esc-mode-link">{t("isqmPage.escModeLink")}</button>
          </div>
          {mode === "create" ? (
            <>
              <div className="rounded border bg-slate-50 p-2 text-[11px] text-muted-foreground dark:bg-slate-900/40">
                <div><b>{t("isqmPage.escInspRefLabel")}</b> {inspection.engagement_inspected} · {inspection.date || "—"}</div>
                <div><b>{t("isqmPage.escSourceLabel")}</b> {t("isqmPage.escSourceValue")}</div>
                <div className="mt-1">{t("isqmPage.escNoCarryPre")} <b>{t("isqmPage.escNoCarryBold")}</b> {t("isqmPage.escNoCarryPost")}</div>
              </div>
              <div>
                <Label>{t("isqmPage.escLabelDesc")}</Label>
                <textarea rows={3} className="w-full rounded-md border p-2 text-sm dark:bg-slate-900"
                          value={desc} onChange={(e) => setDesc(e.target.value)}
                          data-testid="esc-desc" />
              </div>
            </>
          ) : (
            <div>
              <Label>{t("isqmPage.escLabelPick")}</Label>
              <Select value={defId} onValueChange={setDefId}>
                <SelectTrigger data-testid="esc-def-picker">
                  <SelectValue placeholder={t("isqmPage.escPickerPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {defs.filter((d) => d.status !== "remediated").map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      [{d.status}] {d.description.slice(0, 80)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("isqmPage.cancel")}</Button>
          <Button onClick={submit} className="bg-slate-900 hover:bg-slate-800" data-testid="esc-submit">
            {t("isqmPage.escConfirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { RegistersSection };
